import { bpsToPercent, percentToBps, toBig } from '@src/utils/number';
import type { Paths } from '@src/utils/types';
import { cloneDeep, get, isEqual, set } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isBigCompatible } from '../../../utils/big';
import type { EntityAdminClass } from '../EntityAdminClass';
import { isNotDivider } from '../components/InputsAndDropdownsDrawer/components/dividerUtils';
import { getDrawerContentRenderRule } from '../components/InputsAndDropdownsDrawer/components/registry';
import { flattenDrawerOptions } from '../components/InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import type {
  InputNumericDrawerOption,
  InputsAndDropdownsDrawerOption,
  InputsAndDropdownsDrawerOptions,
} from '../components/InputsAndDropdownsDrawer/types';
import type { EntityAdminRecord } from '../types';
import { extractFieldsFromDrawerOptions } from '../utils';

const applyNumericVariantTransformToControl = <TDrawerRecord extends EntityAdminRecord>(
  value: unknown,
  option: InputNumericDrawerOption<TDrawerRecord>
): unknown => {
  if (option.variant === 'bps') {
    return isBigCompatible(value) ? percentToBps(value) : undefined;
  }
  if (option.variant === 'percent') {
    return toBig(value)?.mul(100).toFixed();
  }
  return value;
};

const applyNumericVariantTransformToRecord = <TDrawerRecord extends EntityAdminRecord>(
  value: unknown,
  option: InputNumericDrawerOption<TDrawerRecord>
): unknown => {
  if (option.variant === 'bps') {
    return isBigCompatible(value) ? bpsToPercent(value) : value;
  }
  if (option.variant === 'percent') {
    return toBig(value)?.div(100).toFixed() ?? '';
  }
  return value;
};

const applyFieldTransformations = <TDrawerRecord extends EntityAdminRecord>(
  value: TDrawerRecord[keyof TDrawerRecord],
  drawerOption: InputsAndDropdownsDrawerOption<TDrawerRecord> | undefined
): TDrawerRecord[keyof TDrawerRecord] => {
  if (drawerOption) {
    if (isNotDivider(drawerOption) && drawerOption.transformDrawerRecordToControl) {
      return drawerOption.transformDrawerRecordToControl(value);
    }
    if (drawerOption.type === 'inputNumeric') {
      return applyNumericVariantTransformToControl(value, drawerOption) as TDrawerRecord[keyof TDrawerRecord];
    }
    const renderType = getDrawerContentRenderRule(drawerOption.type);
    if (renderType?.transformDrawerRecordToControl) {
      return renderType.transformDrawerRecordToControl(value);
    }
  }
  return value;
};

const getFormState = <TDrawerRecord extends EntityAdminRecord>(
  entity: TDrawerRecord | undefined,
  drawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[]
): TDrawerRecord => {
  if (!entity) {
    return {} as TDrawerRecord;
  }
  const fieldsWeCareAbout = extractFieldsFromDrawerOptions(drawerOptions);
  return fieldsWeCareAbout.reduce((acc, field) => {
    const drawerOption = drawerOptions.find(option => isNotDivider(option) && option.field === field);
    if (get(entity, field) != null) {
      set(acc, field, applyFieldTransformations(get(entity, field), drawerOption));
    }

    return acc;
  }, {} as TDrawerRecord);
};

/**
 * Determines if a field should be skipped during validation based on visibleTabs.
 *
 * @param drawerTabs - The tabs this field belongs to (if any)
 * @param visibleTabs - The currently visible tabs (if any)
 * @returns true if validation should be skipped, false otherwise
 */
const shouldSkipFieldValidation = (drawerTabs: string[] | undefined, visibleTabs: string[] | undefined): boolean => {
  // Fields without drawerTabs are always validated
  if (!drawerTabs?.length) {
    return false;
  }

  // If visibleTabs is undefined or empty, validate all fields (prevents race condition)
  if (!visibleTabs || visibleTabs.length === 0) {
    return false;
  }

  // Skip validation only if field doesn't belong to any visible tab
  return !drawerTabs.some(tab => visibleTabs.includes(tab));
};

const useEntityAdminFormValidation = <TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOption<TDrawerRecord>[],
  form: TDrawerRecord,
  originalEntity: TDrawerRecord | undefined,
  /**
   * The tabs that are currently visible (and will be validated) from the CustomDrawerTabs component.
   * If not provided, all fields will be validated against all tabs.
   * - this allows for validations of different scenarios of tabs
   */
  visibleTabs?: string[],
  isEditing?: boolean
) => {
  const [touchState, setTouchState] = useState<Record<string, boolean>>(() => {
    const initialTouchState: Record<string, boolean> = {};
    drawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        initialTouchState[option.field] = false;
      }
    });
    return initialTouchState;
  });

  const touchAll = useCallback(() => {
    const newTouchState: Record<string, boolean> = {};
    drawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        newTouchState[option.field] = true;
      }
    });
    setTouchState(newTouchState);
  }, [drawerOptions]);

  const touchField = useCallback((field: string) => {
    setTouchState(prev => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  const errorState = useMemo(() => {
    const result: Record<string, string | undefined> = {};
    drawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        // [DEAL-5742] Skip error state validation for hidden fields
        if (option.getIsHidden?.(form, originalEntity)) {
          return;
        }
        if (shouldSkipFieldValidation(option.drawerTabs, visibleTabs)) {
          return;
        }

        if (option.validate) {
          // The DrawerOption has its own validation. Run this instead of the basic validation.
          // Calculate isDisabled to pass to validate function
          const isDisabled = (option.disabledWhenEditing && isEditing) || option.getIsDisabled?.(form) || false;
          const validationResult = option.validate(form, isDisabled);
          if (validationResult != null) {
            result[option.field] = validationResult;
          }
        } else {
          const isRequired = option.getIsRequired?.(form);
          const isPopulated = option.isValueProvided
            ? option.isValueProvided(form, option.field)
            : defaultIsValueProvided(form, option.field);

          if (isRequired && !isPopulated) {
            result[option.field] = 'This field is required';
          }
        }
      }
    });
    return Object.keys(result).length > 0 ? result : null;
  }, [drawerOptions, form, originalEntity, visibleTabs, isEditing]);

  return { touchState, touchAll, touchField, errorState };
};

export type EntityAdminFormState<TDrawerRecord extends EntityAdminRecord> = {
  form: TDrawerRecord;
  handleOnFormUpdate: (field: Paths<TDrawerRecord>, value: unknown, suppressTouch?: boolean) => void;
  inheritedValues: Record<Paths<TDrawerRecord>, string | undefined>;
  someError: boolean;
  prepareFormForSave: () => TDrawerRecord;
  originalEntity: TDrawerRecord | undefined;
  errorState: Record<string, string | undefined> | null;
  touchState: Record<string, boolean>;
  touchAll: () => void;
};

/**
 * Hook functionality to get the form state, update the form state, retrieve inherited values,
 * check if the form is dirty, and prepare the form for saving.
 *
 * @param param0 - The parameters for the hook
 * @returns An object containing:
 *   - formState: The current state of the form, derived from the entity and drawer options.
 *   - inheritedValues: Values inherited from the selected entities.
 *   - prepareFormForSave: A function that prepares the form data for saving by applying necessary transformations.
 */
type GetEntityAsDrawerEntity<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord> = (
  entity: TRecord,
  classEntity?: EntityAdminClass<TRecord>
) => TDrawerRecord;

export function useEntityAdminFormState<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  selectedEntities,
  drawerOptions,
  getEntityAsDrawerEntity,
  setIsEntityAdminDrawerDirty,
  newEntityDefault,
  visibleTabs,
  isEditing,
}: {
  selectedEntities: EntityAdminClass<TRecord>[];
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>;
  getEntityAsDrawerEntity: GetEntityAsDrawerEntity<TRecord, TDrawerRecord>;
  setIsEntityAdminDrawerDirty: (dirty: boolean) => void;
  newEntityDefault?: () => Partial<TDrawerRecord>;
  visibleTabs?: string[];
  isEditing?: boolean;
}): EntityAdminFormState<TDrawerRecord> {
  const flatDrawerOptions = useMemo(() => flattenDrawerOptions(drawerOptions), [drawerOptions]);
  const firstEntity = selectedEntities.at(0)?.data;

  const [form, setForm] = useState<TDrawerRecord>(() => {
    if (firstEntity == null) {
      // Get current grid data from the blotter table API
      return (newEntityDefault?.() ?? {}) as TDrawerRecord;
    }

    const fieldsWeCareAbout = extractFieldsFromDrawerOptions(flatDrawerOptions);
    // Loop through each field and test if all values are the same from every selected entity
    const bulkEditFormInitialState = fieldsWeCareAbout.reduce((acc, field) => {
      const allValues = selectedEntities.map(entity => get(getEntityAsDrawerEntity(entity.data), field));
      const firstValue = get(getEntityAsDrawerEntity(firstEntity), field);
      const allValuesAreSame = allValues.every(v => isEqual(v, firstValue));
      if (allValuesAreSame) {
        set(acc, field, firstValue);
      }
      return acc;
    }, {} as TDrawerRecord);
    return getFormState(bulkEditFormInitialState, flatDrawerOptions);
  });

  const getCollectivePlaceholderLabel = useCallback(
    (drawerOption: InputsAndDropdownsDrawerOption<TDrawerRecord>): string | undefined => {
      if (!isNotDivider(drawerOption)) {
        return undefined;
      }
      const parentValue = selectedEntities.every(entity => entity.isFieldInherited(drawerOption.field))
        ? (get(selectedEntities.at(0), drawerOption.field) as TDrawerRecord[keyof TDrawerRecord])
        : undefined;

      // [DEAL-5278] We can inherit "false" boolean values from the parent entity
      return parentValue != null ? applyFieldTransformations(parentValue, drawerOption) : undefined;
    },
    [selectedEntities]
  );

  const inheritedValues = useMemo(() => {
    const values: Record<Paths<TDrawerRecord>, string | undefined> = {} as Record<
      Paths<TDrawerRecord>,
      string | undefined
    >;
    flatDrawerOptions.forEach(option => {
      if (isNotDivider(option)) {
        values[option.field] = getCollectivePlaceholderLabel(option);
      }
    });
    return values;
  }, [flatDrawerOptions, getCollectivePlaceholderLabel]);

  const prepareFormForSave = useCallback(() => {
    const formWithInputTypesConverted = flatDrawerOptions.reduce((acc, option) => {
      if (!isNotDivider(option)) {
        return acc;
      }
      const formField = get(form, option.field);
      if (formField != null) {
        const renderType = getDrawerContentRenderRule<TDrawerRecord>(option.type);

        // Transform the form control values back to the drawer record
        if (option.transformControlToDrawerRecord) {
          set(acc, option.field, option.transformControlToDrawerRecord(formField));
        } else if (option.type === 'inputNumeric') {
          set(acc, option.field, applyNumericVariantTransformToRecord(formField, option));
        } else if (renderType?.transformControlToDrawerRecord) {
          set(acc, option.field, renderType.transformControlToDrawerRecord(formField));
        }
      }
      return acc;
    }, cloneDeep(form));

    return formWithInputTypesConverted;
  }, [flatDrawerOptions, form]);

  useEffect(() => {
    if (firstEntity == null) {
      setIsEntityAdminDrawerDirty(false);
    } else {
      const firstClassEntity = selectedEntities.at(0);
      const drawerEntity = getEntityAsDrawerEntity(firstEntity, firstClassEntity);
      setIsEntityAdminDrawerDirty(!isEqual(form, getFormState(drawerEntity, flatDrawerOptions)));
    }
  }, [flatDrawerOptions, firstEntity, form, getEntityAsDrawerEntity, setIsEntityAdminDrawerDirty, selectedEntities]);

  const originalEntity = useMemo(() => {
    if (firstEntity == null) {
      return undefined;
    }
    const firstClassEntity = selectedEntities.at(0);
    return getEntityAsDrawerEntity(firstEntity, firstClassEntity);
  }, [firstEntity, getEntityAsDrawerEntity, selectedEntities]);

  const { touchAll, touchField, errorState, touchState } = useEntityAdminFormValidation(
    flatDrawerOptions,
    form,
    originalEntity,
    visibleTabs,
    isEditing
  );

  const handleOnFormUpdate = useCallback(
    (field: Paths<TDrawerRecord>, value: unknown, suppressTouch = false) => {
      if (!suppressTouch) {
        touchField(field);
      }
      setForm(prev => {
        const next = cloneDeep(prev);
        set(next, field, value);
        return next;
      });
    },
    [touchField]
  );

  return {
    form,
    handleOnFormUpdate,
    inheritedValues,
    someError: errorState != null, // errorState is returned as null if there are no entries in it
    prepareFormForSave,
    originalEntity,
    touchState,
    errorState,
    touchAll,
  };
}

function defaultIsValueProvided<TDrawerRecord extends EntityAdminRecord>(form: TDrawerRecord, formField: string) {
  const value = get(form, formField);
  const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
  return !isEmpty;
}
